import { AddToCartButtonType } from '@commerce-cart-webcomponents/ui-composite';
import {
  checkForPartialBackOrder,
  DerivedAvailabilityStatusInternal,
  ProductData,
  Slot,
  type IProduct,
} from '@commerce-product-webcomponents/sdk';
import {
  AddToWishlistContainer,
  CardType,
  ConditionalButtonComponent,
  getDesignatedOptionMap,
  ProductCardComponent,
  userSessionRestriction,
  VariantDrawer,
} from '@commerce-product-webcomponents/ui';
import { ButtonSize, useSession, useTranslation } from '@nextgen-web-framework/all';
import { useEffect, useState } from 'react';
import { type BundleSlotEntriesType, type BundleSlotType } from '../ProductContainer/ProductContainer';
import styles from './ProductMerchandise.module.scss';
import { type ProductMerchandisePropsTypes } from './types/propsTypes';

const ProductMerchandise = (props: ProductMerchandisePropsTypes): JSX.Element => {
  const {
    strikethroughPrice,
    disabled,
    entryObj,
    className = '',
    merchandiseClassName = '',
    size,
    productData,
    showZeroAsFree = false,
    isPrefixFlag = false,
    prefixText,
    showTags = true,
    showPreLaunch,
  } = props;
  const {
    mainLink,
    alt,
    brandName,
    productName,
    productPrice,
    currency,
    productSize,
    pointValue,
    businessVolume,
    variantData,
    productId,
    derivedAvailabilityStatus,
    isBundle,
    bundle,
    minPrice,
    maxPrice,
    productType,
    productRetailPrice,
    minRetailPrice,
    maxRetailPrice,
    productTags,
  } = productData;
  const t = useTranslation();
  const { session, isAnonymous, isABO } = useSession();
  const { productFlags = {} } = productData || {};
  const userRestrictionFlag = userSessionRestriction(session, productFlags, isAnonymous, isABO);

  const bundles = bundle;
  const bundleSlotsState: BundleSlotType[] = bundles?.slots?.map((slot: Slot) => {
    const bundleSlot: BundleSlotType = {
      id: slot.id?.toString() as string,
      entries: slot.products?.map((product: ProductData) => {
        const bundleSlotEntry = {
          product: {
            id: product.items[0].itemNumber as string,
          },
          quantity: slot.minQty,
        };
        return bundleSlotEntry;
      }) as unknown as BundleSlotEntriesType[],
    };
    return bundleSlot;
  }) as BundleSlotType[];
  const [openVariantDrawer, setOpenVariantDrawer] = useState<boolean>(false);
  const [newBundleDerivedStatus, setNewBundleDerivedStatus] = useState<DerivedAvailabilityStatusInternal>(
    derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
  );
  const designatedOptionMap = getDesignatedOptionMap(productId, variantData);
  const formattedVariantName = designatedOptionMap ? designatedOptionMap.optionChoice : '';
  useEffect(() => {
    const prodData = { bundle, derivedAvailabilityStatus, isBundle };
    setNewBundleDerivedStatus(checkForPartialBackOrder(prodData as IProduct));
  }, []);
  const variantDrawerHandler = () => {
    setOpenVariantDrawer(true);
  };
  const derivedIsBundle = !!isBundle;
  return (
    <>
      {openVariantDrawer && (
        <VariantDrawer
          link={mainLink}
          alt={alt}
          brandName={brandName}
          productName={productName}
          productPrice={productPrice}
          currency={currency}
          productId={productId}
          productSize={productSize}
          onClose={() => {
            setOpenVariantDrawer(false);
          }}
          derivedAvailabilityStatus={derivedAvailabilityStatus}
          addToCartConfig={{
            className,
            entryObj,
            productData,
          }}
          showZeroAsFree={showZeroAsFree}
          productData={productData}
          showPreLaunch={showPreLaunch}
        />
      )}
      <div className={`${styles['cpw-product-merchandise']} ${merchandiseClassName}`}>
        <ProductCardComponent
          brandName={brandName}
          productName={productName}
          link={mainLink}
          alt={alt}
          productId={productId}
          productSize={productSize}
          derivedAvailabilityStatus={newBundleDerivedStatus}
          productPrice={productPrice}
          currency={currency}
          cardType={CardType.PRODUCTMERCHANDISE}
          strikethroughPrice={strikethroughPrice}
          variantName={formattedVariantName}
          minPrice={minPrice}
          maxPrice={maxPrice}
          productRetailPrice={productRetailPrice}
          minRetailPrice={minRetailPrice}
          maxRetailPrice={maxRetailPrice}
          isBundle={derivedIsBundle}
          pointValue={pointValue}
          businessVolume={businessVolume}
          handleDropdown={variantDrawerHandler}
          productType={productType}
          wishListEle={
            <span className={styles['heart-icon']}>
              <AddToWishlistContainer
                isPDP={false}
                isPLPCard={true}
                productData={productData}
                isBundle={derivedIsBundle}
              />
            </span>
          }
          buttonEle={
            <ConditionalButtonComponent
              derivedAvailabilityStatus={derivedAvailabilityStatus}
              disabled={disabled || userRestrictionFlag}
              entryObj={entryObj}
              bundleSlots={bundleSlotsState}
              className={styles['product-merchandise-conditional-btn']}
              size={size || ButtonSize.SMALL}
              addToCartButtonType={AddToCartButtonType.ADD_TO_CART_BUTTON}
              productData={productData}
            />
          }
          showZeroAsFree={showZeroAsFree}
          prefixText={isPrefixFlag ? prefixText || t('product.composite.common.item') : ''}
          productTags={productTags}
          showTags={showTags}
        />
      </div>
    </>
  );
};

export default ProductMerchandise;
export type { ProductMerchandisePropsTypes };
